import { Rng } from '../../core/rng';
import { DungeonBuilder, type DungeonKit } from '../../zones/DungeonBuilder';
import type { ZoneLayout } from '../../zones/types';

const ICESTONE = 0xd8e4f0;
const FROST_ROCK = 0xe0e8f4;
const BANNER = 0x6f8fd0;

/** Frost-rimed fortress kit: crenellated walls, columns, ice crystals in the rock. */
const KIT: DungeonKit = {
  wall: { model: 'wall_segment', length: 8, depth: 1.2, scale: 0.8, tint: ICESTONE },
  post: { model: 'pillar_intact', every: 3, scale: [0.85, 1.0], tint: ICESTONE },
  light: { model: 'brazier', every: 3 },
  low: [
    { model: 'rock_b', scale: [0.7, 1.1], tint: FROST_ROCK, weight: 2 },
    { model: 'ice_crystal', scale: [0.35, 0.55] },
    { model: 'pillar_ruin', scale: [0.5, 0.7], tint: ICESTONE },
  ],
  rocks: [
    { model: 'cliff_chunk', r: 2.9, scale: [0.45, 0.85], tint: FROST_ROCK, weight: 3 },
    { model: 'ice_crystal', r: 0.8, scale: [0.8, 1.5], weight: 1.5 },
    { model: 'rock_c', r: 0.7, scale: [1.0, 1.8], tint: FROST_ROCK },
  ],
  clutter: [{ model: 'rock_b', scale: [0.3, 0.5], tint: FROST_ROCK, weight: 2 }, { model: 'ice_crystal', scale: [0.2, 0.35] }, { model: 'bones', weight: 0.5 }],
  clutterChance: 0.25,
};

/**
 * FROSTFANG KEEP (Act III story dungeon, levels 27–29). The Hollow Legion holds
 * a mountain fortress: gatehouse → snowbound courtyard → grand stair → great
 * hall (dark armory off-path) → rampart walk → Hall of Frozen Kings → the
 * Matriarch's Rime Throne (r 17). Floors climb as the keep rises.
 *
 * Spawns: `start`, `boss`. Exits: `keep_exit` in the gatehouse and
 * `keep_exit_boss` at the throne (hidden until `keep_cleared`), both to
 * Frosthollow `fromKeep`.
 */
export function buildKeep(): ZoneLayout {
  const rng = new Rng('keep');
  const d = new DungeonBuilder(
    {
      id: 'keep', name: 'Frostfang Keep', subtitle: 'Seat of the Frost Matriarch', kind: 'dungeon', biome: 'frost',
      levelRange: [27, 29], music: 'dungeon', width: 180, depth: 224, seed: 2401,
      ground: ['cobble', 'snow', 'rock', 'ice'], groundScale: [4, 6, 7, 6], ambient: 'snow', mapTint: 0x2a3444,
      wallHeight: 7.5, nearWallHeight: 1.2, accentAmount: 0.45, paint: { floor: 0, path: 1, wall: 2, accent: 1 }, kit: KIT,
    },
    rng,
  );

  d.hall(0, 88, 7, 7, { y: 0.8, path: true }); // gatehouse
  d.hall(0, 60, 22, 15, { y: 0, path: true, layer: 1 }); // courtyard (snow)
  d.hall(0, 12, 10, 19, { y: 1.2, path: true }); // great hall
  d.hall(-24, 6, 5, 6, { y: 1.2 }); // armory (off-path)
  d.room(18, -28, 7, { y: 2, path: true }); // rampart landing
  d.room(8, -54, 9, { y: 2.6, path: true }); // hall of frozen kings
  d.room(0, -84, 17, { y: 3.2, path: true }); // rime throne
  d.corridor([{ x: 0, z: 82 }, { x: 0, z: 73 }], 6, { path: true });
  d.corridor([{ x: 0, z: 46 }, { x: 0, z: 30 }], 8, { path: true, layer: 1 });
  d.corridor([{ x: -9, z: 6 }, { x: -20, z: 6 }], 3.5);
  d.corridor([{ x: 0, z: -6 }, { x: 0, z: -14 }, { x: 14, z: -24 }], 5, { path: true });
  d.corridor([{ x: 17, z: -34 }, { x: 11, z: -47 }], 5, { path: true });
  d.corridor([{ x: 5, z: -62 }, { x: 1, z: -69 }], 6, { path: true });

  // Gatehouse.
  d.spawn('start', 0, 86, Math.PI);
  d.litPortal({ id: 'keep_exit', to: 'frosthollow', toSpawn: 'fromKeep', x: 0, z: 92.4, radius: 2.2, label: 'Frosthollow', kind: 'exit' }, 0);
  d.prop('banner_pole', -5.5, 84, { collider: { r: 0.35 }, tint: BANNER });
  d.prop('banner_pole', 5.5, 84, { collider: { r: 0.35 }, tint: BANNER });
  d.arch(0, 80.6, Math.PI, 6, ICESTONE);

  // Courtyard: frozen fountain, snowy pines, supply stacks.
  d.prop('fountain', 0, 60, { collider: { r: 2.3 }, tint: 0xe8f4ff });
  for (const [x, z] of [[-18, 70], [18, 70], [-18.5, 49], [18.5, 49]] as const) d.prop('tree_pine_snow', x, z, { scale: 1.15, rot: x * z, collider: { r: 0.5 } });
  d.prop('crate', 15, 72.5, { collider: { w: 0.9, d: 0.9 } });
  d.prop('crate', 16.1, 72.2, { rot: 0.4, collider: { w: 0.9, d: 0.9 } });
  d.prop('barrel', 14.2, 71.4, { collider: { r: 0.45 } });
  d.prop('cart', -15, 72, { rot: 1.3, collider: { w: 1.8, d: 3.4 }, tint: 0xc8d0dc });
  d.prop('barrel', -19.5, 55, { collider: { r: 0.45 } });
  d.prop('barrel', -19.4, 56.1, { collider: { r: 0.45 } });
  d.pack({ id: 'keep_yard_west', monsters: [{ def: 'frost_ghoul', count: 4 }], x: -11, z: 60, radius: 4.5, level: 27 });
  d.pack({ id: 'keep_yard_east', monsters: [{ def: 'frost_ghoul', count: 3 }, { def: 'ice_wraith', count: 1 }], x: 11, z: 56, radius: 4.5, level: 27 });

  // Great hall: column rows, banners, statues at the north doors.
  for (const z of [24, 17, 10, 3]) for (const x of [-5.5, 5.5]) d.prop('pillar_intact', x, z, { collider: { r: 0.6 }, tint: ICESTONE });
  for (const z of [20.5, 6.5]) for (const x of [-8.8, 8.8]) d.prop('banner_pole', x, z, { collider: { r: 0.35 }, tint: BANNER });
  d.prop('statue_hero', -6.5, -3.5, { scale: 0.5, collider: { w: 2.6, d: 2.6 }, tint: ICESTONE });
  d.prop('statue_hero', 6.5, -3.5, { scale: 0.5, collider: { w: 2.6, d: 2.6 }, tint: ICESTONE });
  d.arch(0, 29.8, Math.PI, 8, ICESTONE);
  d.pack({ id: 'keep_hall_guard', monsters: [{ def: 'hollow_knight', count: 1 }, { def: 'frost_ghoul', count: 3 }], x: 0, z: 20, radius: 4, level: 28 });
  d.pack({ id: 'keep_hall_wraiths', monsters: [{ def: 'ice_wraith', count: 2 }, { def: 'frost_ghoul', count: 2 }], x: 0, z: 5, radius: 4, level: 28 });

  // Armory (off-path, unlit): a sealed gate and a weapons chest.
  d.interact({ id: 'keep_chest_armory', kind: 'chest', x: -26, z: 8, data: { loot: 'keep_chest' } });
  d.prop('gate_iron', -29.7, 6, { rot: Math.PI / 2, tint: ICESTONE });
  d.prop('crate', -26.5, 2, { collider: { w: 0.9, d: 0.9 } });
  d.prop('barrel', -25.2, 1.6, { collider: { r: 0.45 } });
  d.prop('forge_anvil', -22, 10.4, { collider: { w: 2.2, d: 1.2 } });

  // Rampart walk and landing.
  d.pack({ id: 'keep_rampart', monsters: [{ def: 'frost_ghoul', count: 2 }, { def: 'ice_wraith', count: 1 }], x: 18, z: -28, radius: 3.5, level: 28 });

  // Hall of Frozen Kings.
  for (const h of [2, 4, 8, 10]) {
    const p = DungeonBuilder.clock(8, -54, 6.8, h);
    d.prop('statue_hero', p.x, p.z, { scale: 0.5, rot: Math.atan2(8 - p.x, -54 - p.z), collider: { w: 2.6, d: 2.6 }, tint: ICESTONE });
  }
  d.pack({ id: 'keep_kings', monsters: [{ def: 'hollow_knight', count: 2 }, { def: 'ice_wraith', count: 1 }], x: 8, z: -53, radius: 3.5, level: 29 });

  // The Rime Throne (r 17).
  d.paintCircle(0, -84, 10, 3, 2);
  d.spawn('boss', 0, -86, 0);
  d.pack({ id: 'keep_boss', monsters: [{ def: 'frost_matriarch', count: 1 }], x: 0, z: -86, radius: 1, level: 29, tags: ['boss'] });
  d.litPortal({ id: 'keep_exit_boss', to: 'frosthollow', toSpawn: 'fromKeep', x: 0, z: -97.6, radius: 2.4, label: 'Return to Frosthollow', kind: 'exit', requires: 'keep_cleared' }, Math.PI);
  d.prop('statue_hero', 0, -104.5, { scale: 0.95, tint: 0xe8f4ff });
  for (const h of [9, 10.5, 1.5, 3]) {
    const p = DungeonBuilder.clock(0, -84, 18.8, h);
    d.prop('ice_crystal', p.x, p.z, { scale: 1.3, rot: h });
  }

  return d.build();
}
